import { mutationOptions, queryOptions } from '@tanstack/react-query'
import { QueryError } from 'src/queries/network.utils'
import {
  GetReceiptRulesResponse,
  MutateReceiptRulesResponse,
  ReceiptRulesBody,
} from 'src/queries/organizations/receiptRules/receiptRules.models'
import {
  getReceiptRules,
  postReceiptRules,
  putReceiptRules,
} from 'src/queries/organizations/receiptRules/receiptRules.network'
import { orgQueryKeys, queryCacheOptions } from 'src/queries/queries.constants'

export const orgReceiptRulesQueryKeys = {
  /**
   * Delegates to the shared constants factory so the key stays
   * ['organizations', orgId, 'receiptrules'] — byte-identical to the cache
   * entries the legacy useQueryOrgReceiptRules hook
   * (src/queries/organizations/receiptRules.queries.tsx) wrote, so the swap
   * never double-fetches or strands a cache entry.
   */
  detail: (orgId: string | undefined) => orgQueryKeys.receiptRules(orgId),
}

export const orgReceiptRulesQueries = {
  detail: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgReceiptRulesQueryKeys.detail(orgId),
      queryFn: () => getReceiptRules(orgId!),
      enabled: Boolean(orgId),
      /**
       * queryCacheOptions (15m stale, no auto refetch) — stated reason: redux
       * parity. Most legacy dispatch sites guarded on
       * `organization?.receiptRule === undefined` (fetch-once semantics), but
       * getOrganization(id, true) also refetched the rules on every org
       * settings page mount, so the data was periodically refreshed within a
       * session. 15 minutes keeps that periodic freshness while deduping the
       * mount-storm of the seven reader surfaces; mutations update the cache
       * explicitly (see the form's onSuccess).
       */
      ...queryCacheOptions(),
    }),
}

export type MutateReceiptRulesParams = {
  orgId: string
  body: ReceiptRulesBody
}

/**
 * The legacy thunks threaded `closeReceiptRulesForm` and an
 * `updateTransactionTable` flag through redux. Those side effects are now
 * owned by the call site (OrgSettingsReceiptRulesForm): close the form,
 * merge the returned rule into the detail cache (the redux reducer merged it
 * into listResponses[orgId] without refetching), and invalidate
 * transactionsQueryKeys.all when the form was opened from the transactions
 * page (parity with the deleted updatedReceiptRules flag that
 * ReportTransactionsBoxAndLinks translated into the same invalidation).
 */
export const orgReceiptRulesMutations = {
  post: () =>
    mutationOptions<
      MutateReceiptRulesResponse,
      QueryError,
      MutateReceiptRulesParams
    >({
      mutationFn: (params) => postReceiptRules(params.orgId, params.body),
    }),
  put: () =>
    mutationOptions<
      MutateReceiptRulesResponse,
      QueryError,
      MutateReceiptRulesParams
    >({
      mutationFn: (params) => putReceiptRules(params.orgId, params.body),
    }),
}

/**
 * Cache-merge helper for mutation onSuccess: POST/PUT return only
 * `receiptRule`, while the detail cache also holds hasRecurringCards and
 * currencies from the GET — merge, never replace (the redux reducer spread
 * `...action.payload` over the existing org record the same way).
 */
export const mergeReceiptRuleIntoDetail = (
  existing: GetReceiptRulesResponse | undefined,
  receiptRule: MutateReceiptRulesResponse['receiptRule'],
): GetReceiptRulesResponse | undefined =>
  existing === undefined ? undefined : { ...existing, receiptRule }
